import { AGUAppModuleDefinition } from 'agu_app_shell';
import { RA_MODULE_KEYS } from './ra-module-keys';
import { RaDashboardComponent } from './modules/dashboard/ra-dashboard.component';
import { GrantFinderPageComponent } from './modules/grant-finder/grant-finder-page.component';
import { PeopleFinderPageComponent } from './modules/people-finder/people-finder-page.component';
import { JobFinderPageComponent } from './modules/job-finder/job-finder-page.component';
import { SavedPageComponent } from './modules/saved/saved-page.component';
import { MyProfilePageComponent } from './modules/profile/my-profile-page.component';
import { SettingsPageComponent } from './modules/settings/settings-page.component';
import { SearchPageComponent } from './modules/search/search-page.component';
import { CareerExplorerPageComponent } from './modules/career-explorer/career-explorer-page.component';

/**
 * Every Research Accelerator module, registered once and composed by metadata.
 *
 * ENTITLEMENT. All four carry an empty `RequiredRoleNames`, which means "open to
 * anyone who holds the Application". That is the correct default for a
 * member-facing product: the Application grant IS the entitlement, and it is
 * controlled per-app by the ApplicationRole rows in metadata/application-roles
 * plus the UserApplication grants. Adding a role name here narrows a module
 * FURTHER, inside apps the user already holds — which is what to reach for when a
 * single module needs to be limited (a beta, an admin-only tool), not for
 * gating whole apps.
 *
 * Note this is not a security boundary on data: every module's reads go through
 * MJ's provider or a resolver that authorises the signed-in user server-side.
 * These flags decide what UI a member is offered.
 *
 * PRESENTATION. `NavLabel`, `HomeTile`, `Accent` and `HomeCopy` are the portal's
 * visual contract, and they live here for the same reason the composition does:
 * which modules Home offers, in what colour and with what copy, is metadata a
 * screen READS, never a switch statement a screen owns. Values are the mockups'
 * own (plans/research-accelerator/mockups/parity/home.md §4.1 for the rail,
 * §4.4 for the tiles). `Secondary`'s `{N}` is substituted with that module's
 * live recommendation count by the Home dashboard.
 */
export const RA_MODULE_DEFINITIONS: readonly AGUAppModuleDefinition[] = [
    {
        Key: RA_MODULE_KEYS.Dashboard,
        Title: 'Home',
        Icon: 'fa-solid fa-compass',
        IconName: 'home',
        Description: 'Your starting point across the Research Accelerator.',
        DefaultView: 'home',
        RootComponent: RaDashboardComponent,
        RequiredRoleNames: [],
        HomeTile: false,
        Accent: 'blue',
        NavTone: 'sky', // P/Member_Hub.html:1891
    },
    {
        Key: RA_MODULE_KEYS.Grants,
        Title: 'Grant Finder',
        NavLabel: 'Grants',
        Icon: 'fa-solid fa-hand-holding-dollar',
        IconName: 'grants',
        Description: 'Funding opportunities matched to your research, filterable by discipline, funder type and country.',
        DefaultView: 'search',
        RootComponent: GrantFinderPageComponent,
        RequiredRoleNames: [],
        HomeTile: true,
        Accent: 'teal',
        HomeCopy: {
            Body: 'Find funding matched to your research profile and track deadlines.',
            Primary: 'Search grants',
            Secondary: 'See {N} recommended',
            PrimaryIcon: 'search',
        },
    },
    {
        Key: RA_MODULE_KEYS.People,
        Title: 'People Finder',
        NavLabel: 'People', // P/Member_Hub.html:1041
        Icon: 'fa-solid fa-user-group',
        IconName: 'collaborators',
        Description: 'Paste an abstract or topic to surface experts for your next proposal.',
        DefaultView: 'search',
        RootComponent: PeopleFinderPageComponent,
        RequiredRoleNames: [],
        HomeTile: true,
        Accent: 'blue',
        HomeCopy: {
            Body: 'Paste an abstract or topic to surface experts for your next proposal.',
            Primary: 'Search people', // P/Member_Hub.html:1076
            Secondary: 'See {N} recommended',
            PrimaryIcon: 'search',
        },
    },
    {
        Key: RA_MODULE_KEYS.Jobs,
        Title: 'Job Finder',
        NavLabel: 'Jobs',
        Icon: 'fa-solid fa-briefcase',
        IconName: 'jobs',
        Description: 'Current openings matched to your profile and research activity.',
        DefaultView: 'search',
        RootComponent: JobFinderPageComponent,
        RequiredRoleNames: [],
        HomeTile: true,
        Accent: 'orange',
        HomeCopy: {
            Body: 'Browse open roles across the Earth and space sciences.',
            Primary: 'Search jobs',
            Secondary: 'See {N} recommended',
            PrimaryIcon: 'search',
        },
    },
    {
        Key: RA_MODULE_KEYS.CareerMap,
        Title: 'Career Explorer', // P/Member_Hub.html:1041-1048
        Icon: 'fa-solid fa-arrow-trend-up',
        IconName: 'career-map',
        Description: 'See the roles researchers like you moved into, and the skills that opened each door.',
        DefaultView: 'start',
        RootComponent: CareerExplorerPageComponent,
        RequiredRoleNames: [],
        HomeTile: true,
        Accent: 'plum',
        HomeCopy: {
            Body: 'See the roles researchers like you moved into, and the skills that opened each door.',
            Primary: 'Upload your CV', // P/Member_Hub.html:1086
            Secondary: 'Start your career journey', // P/Member_Hub.html:1086
            PrimaryIcon: 'upload',
        },
    },
    {
        Key: RA_MODULE_KEYS.Saved,
        Title: 'Saved',
        Icon: 'fa-regular fa-bookmark',
        IconName: 'saved',
        Description: 'Everything you save, in one place.',
        DefaultView: 'board',
        RootComponent: SavedPageComponent,
        RequiredRoleNames: [],
        HomeTile: false,
        Accent: 'blue',
        NavTone: 'amber', // P/Member_Hub.html:1896
    },
    {
        // Reached only from the sidebar identity block's "View my portal
        // profile" link (RaSurfaceComponent.OpenModule('profile', 'me')) —
        // HiddenFromNav keeps it out of the module rail, same as a person's
        // profile view is not itself a nav destination.
        Key: RA_MODULE_KEYS.Profile,
        Title: 'My profile',
        Icon: 'fa-solid fa-id-card',
        Description: 'How you appear to other members.',
        DefaultView: 'me',
        RootComponent: MyProfilePageComponent,
        RequiredRoleNames: [],
        HomeTile: false,
        Accent: 'blue',
        HiddenFromNav: true,
    },
    {
        Key: RA_MODULE_KEYS.Settings,
        Title: 'Settings',
        Icon: 'fa-solid fa-gear',
        IconName: 'settings',
        Description: 'What the Member Hub knows about you, and where each part is managed.',
        DefaultView: 'preferences',
        RootComponent: SettingsPageComponent,
        RequiredRoleNames: [],
        HomeTile: false,
        Accent: 'blue',
        NavTone: 'slate', // P/Member_Hub.html:1897
    },
    {
        // Reached only from the sidebar search box (RaSurfaceComponent.OpenSearch
        // → OpenModule('search', 'results', query)) — HiddenFromNav for the same
        // reason `profile` is: a navigable destination that is not itself a nav
        // rail entry.
        Key: RA_MODULE_KEYS.Search,
        Title: 'Search',
        Icon: 'fa-solid fa-magnifying-glass',
        Description: 'Search everything across grants, people and jobs.',
        DefaultView: 'results',
        RootComponent: SearchPageComponent,
        RequiredRoleNames: [],
        HomeTile: false,
        Accent: 'blue',
        HiddenFromNav: true,
    },
];
